import React, { useEffect, useRef, useState } from "react";
import { Box, FormControl, FormHelperText, MenuItem, Select, TextField, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import SimpleReactValidator from 'simple-react-validator';
import moment from "moment";

import useStyles from "./Page1.style";
import { repliValidator } from "../../../components";

const Page1 = props => {
    const classes = useStyles();
    const { t } = useTranslation();

    const [dateOfBirth, setDateOfBirth] = useState('');
    const [firstMenstruationAge, setFirstMenstruationAge] = useState(0);
    const [sexualDebutAge, setSexualDebutAge] = useState(0);
    const [menopauseAge, setMenopauseAge] = useState(0);
    const [firstMenstruationYearRange, setFirstMenstruationYearRange] = useState([]);
    const [sexualDebutYearRange, setSexualDebutYearRange] = useState([]);
    const [menopauseYearRange, setMenopauseYearRange] = useState([]);

    useEffect(() => {
        const localStorageDateOfBirth = localStorage.getItem('dateOfBirth');
        const propsDateOfBirth = props.profile && moment.utc(props.profile.dateOfBirth).format('YYYY-MM-DD');
        const storedDateOfBirth = localStorageDateOfBirth || propsDateOfBirth;

        const localStorageFirstMenstruationYear = localStorage.getItem('firstMenstruationAge') && parseInt(localStorage.getItem('firstMenstruationAge'));
        const propsFirstMenstruationYear = props.profile && parseInt(props.profile.firstMenstruationYear);
        const storedFirstMenstruationYear = localStorageFirstMenstruationYear || propsFirstMenstruationYear;

        const localStorageSexualDebutYear = localStorage.getItem('sexualDebutAge') && parseInt(localStorage.getItem('sexualDebutAge'));
        const propsSexualDebutYear = props.profile && parseInt(props.profile.sexualDebutYear);
        const storedSexualDebutYear = localStorageSexualDebutYear || propsSexualDebutYear;

        const localStorageMenopauseYear = localStorage.getItem('menopauseAge') && parseInt(localStorage.getItem('menopauseAge'));
        const propsMenopauseYear = props.profile && parseInt(props.profile.menopauseYear);
        const storedMenopauseYear = localStorageMenopauseYear || propsMenopauseYear;

        setDateOfBirth(storedDateOfBirth);
        setFirstMenstruationAge(storedFirstMenstruationYear);
        setSexualDebutAge(storedSexualDebutYear);
        setMenopauseAge(storedMenopauseYear);
    }, [props.profile]);

    useEffect(() => {
        if (!dateOfBirth) return;

        localStorage.setItem('dateOfBirth', dateOfBirth);

        let yearOfBirth = new Date(dateOfBirth).getFullYear();
        if (!firstMenstruationAge) setFirstMenstruationAge(yearOfBirth + 12);
        setFirstMenstruationYearRange(yearsArray(yearOfBirth + 9, 12));
        setSexualDebutYearRange(yearsArray(yearOfBirth + 10, 30));
        setMenopauseYearRange(yearsArray(yearOfBirth + 20, 30));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateOfBirth]);

    useEffect(() => {
        localStorage.setItem('firstMenstruationAge', firstMenstruationAge);
    }, [firstMenstruationAge])

    const validator = useRef(repliValidator({
        validators: {
            valid_menopause_year: {
                message: t('repliQuestionnaire.validation.menopauseYear'),
                rule: (val, params, validator) => {
                    return (val >= params[0]) || (val === 0) || isNaN(val)
                }
            }
        }
    }));

    const yearsArray = (start, length) => {
        return Array.from({ length: length }, (v, k) => k + start);
    };

    const handleBirthDateChange = e => {
        setDateOfBirth(e.target.value);
    };

    const handleFirstMenstruationYearChange = e => {
        setFirstMenstruationAge(e.target.value);
    };

    const handleSexualDebutYearChange = e => {
        setSexualDebutAge(e.target.value);
        localStorage.setItem('sexualDebutAge', e.target.value);
    };

    const handleMenopauseYearChange = e => {
        const isValid = validator.current.allValid();
        if (!isValid) validator.current.showMessages();
        setMenopauseAge(e.target.value);
        localStorage.setItem('menopauseAge', e.target.value);
    };

    return (
        <>
            <Box className={classes.title}>{t('repliQuestionnaire.welcome')}</Box>
            <Box className={classes.question}>
                <Typography>{t('repliQuestionnaire.general.birthQuestion')}</Typography>
                <TextField
                    variant="outlined"
                    type="date"
                    value={dateOfBirth}
                    onChange={handleBirthDateChange}
                />
            </Box>
            <Box className={classes.question}>
                <Typography>{t('repliQuestionnaire.general.firstMestruationQuestion')}</Typography>
                <FormControl variant="outlined">
                    <Select value={firstMenstruationAge} onChange={handleFirstMenstruationYearChange}>
                        <MenuItem key={0} value={0}></MenuItem>
                        {firstMenstruationYearRange.map(year => {
                            return <MenuItem key={year} value={year}>{year}</MenuItem>
                        })}
                    </Select>
                </FormControl>
            </Box>
            <Box className={classes.question}>
                <Typography>{t('repliQuestionnaire.general.sexualDebutQuestion')}</Typography>
                <FormControl variant="outlined">
                    <Select value={sexualDebutAge} onChange={handleSexualDebutYearChange}>
                        <MenuItem key={0} value={0}></MenuItem>
                        {sexualDebutYearRange.map(year => {
                            return <MenuItem key={year} value={year}>{year}</MenuItem>
                        })}
                    </Select>
                </FormControl>
            </Box>
            <Box className={classes.question}>
                <Typography>{t('repliQuestionnaire.general.menopauseQuestion')}</Typography>
                <FormControl variant="outlined" error={!validator.current.fieldValid('menopauseAge')}>
                    <Select
                        value={menopauseAge}
                        onChange={handleMenopauseYearChange}>
                        <MenuItem key={0} value={0}></MenuItem>
                        {menopauseYearRange.map(year => {
                            return <MenuItem key={year} value={year}>{year}</MenuItem>
                        })}
                    </Select>
                    <FormHelperText>{validator.current.message('menopauseAge', menopauseAge, `valid_menopause_year:${firstMenstruationAge}`)}</FormHelperText>
                </FormControl>
            </Box>
        </>
    )
};

export default Page1;